import { useState } from 'react';
import { usePageMeta, Reveal, FORM_ENDPOINT } from '../lib/site';
import { CardHeader } from '../components/Bits';

const CAN_HELP = [
  'Account and sign-in help',
  'Billing and subscription questions',
  'Bugs, crashes, and anything that looks off',
  'Feature requests and feedback',
  'Account and data deletion requests',
];

export default function Support() {
  usePageMeta(
    'Support | MyGolfLinks',
    'Question about the app, your account, or a round that did not look right? Send us a note and a real person replies within one business day.'
  );
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = Object.fromEntries(new FormData(form).entries());
    if (data.website) {
      setSent(true);
      return;
    }
    setSending(true);
    setErr('');
    try {
      const r = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'support', ...data }),
      });
      if (!r.ok) throw new Error('send_failed');
      setSent(true);
    } catch {
      setErr("That didn't go through. Try again, or email support@mygolflinks.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <section style={{ padding: 'clamp(130px, 15vw, 170px) 0 clamp(70px, 9vw, 110px)' }}>
        <div className="wrap">
          <div className="demo-page-grid">
            <Reveal>
              <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
                Support
              </p>
              <h1 className="h2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.4rem)' }}>
                A real person replies.
              </h1>
              <p className="lede" style={{ margin: '18px 0 36px' }}>
                Question about the app, your account, or a round that didn't look right? Send us a
                note and we reply within one business day. If you're reporting a bug, the more
                detail the better: what you were doing, what you expected, and what actually
                happened.
              </p>
              <CardHeader no="01" label="What we can help with" />
              <div className="ledger">
                {CAN_HELP.map((t) => (
                  <div className="led" key={t} style={{ gridTemplateColumns: '1fr', padding: '14px 0' }}>
                    <span style={{ fontSize: '0.98rem' }}>{t}</span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 26, fontSize: '0.92rem', color: 'var(--mut)' }}>
                Prefer email? Write us at{' '}
                <a href="mailto:support@mygolflinks.com" className="tlink">support@mygolflinks.com</a>.
              </p>
            </Reveal>

            <Reveal d={1}>
              <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 16, padding: 'clamp(24px, 3.5vw, 38px)' }}>
                {sent ? (
                  <div style={{ textAlign: 'center', padding: '40px 10px' }}>
                    <div className="mono" style={{ fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', marginBottom: 14 }}>
                      Message received
                    </div>
                    <h2 className="h3" style={{ fontSize: '1.6rem' }}>Thanks for reaching out.</h2>
                    <p style={{ color: 'var(--mut)', marginTop: 12, maxWidth: '40ch', marginLeft: 'auto', marginRight: 'auto' }}>
                      We'll reply to the email you gave us, usually within one business day.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit}>
                    <div className="g2-tight">
                      <div className="fg">
                        <label htmlFor="s-name">Your name</label>
                        <input className="fi" id="s-name" name="name" type="text" autoComplete="name" required />
                      </div>
                      <div className="fg">
                        <label htmlFor="s-email">Your email</label>
                        <input className="fi" id="s-email" name="email" type="email" autoComplete="email" required />
                      </div>
                    </div>
                    <div className="fg">
                      <label htmlFor="s-topic">What can we help with?</label>
                      <div className="fwrap-select">
                        <select className="fi" id="s-topic" name="topic" required defaultValue="">
                          <option value="" disabled>Pick one</option>
                          <option>Account and sign-in</option>
                          <option>Billing and subscriptions</option>
                          <option>A bug or problem</option>
                          <option>Feature request or feedback</option>
                          <option>Delete my account and data</option>
                          <option>Something else</option>
                        </select>
                      </div>
                    </div>
                    <div className="fg">
                      <label htmlFor="s-message">How can we help?</label>
                      <textarea className="fi" id="s-message" name="message" rows="5" required />
                    </div>
                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <label htmlFor="s-website">Leave this field empty</label>
                      <input id="s-website" name="website" type="text" tabIndex="-1" autoComplete="off" />
                    </div>
                    <button type="submit" className="btn btn-gold f-submit" disabled={sending}>
                      {sending ? 'Sending' : 'Send the message'}
                    </button>
                    {err && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--flag)', marginTop: 12, textAlign: 'center' }}>{err}</p>
                    )}
                    <p style={{ fontSize: '0.8rem', color: 'var(--faint)', textAlign: 'center', marginTop: 12 }}>
                      We'll only use your email to reply to this request.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
